/** React wrapper for Handsontable */
import { forwardRef, useEffect, useRef, useImperativeHandle, type ForwardedRef } from 'react';
import Handsontable from 'handsontable';
import type { GridSettings } from '@/support/hot.types';
import { hotSanitizer } from '@/support/HotSanitizer';
import { _notNil } from '@/littledash';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-classic.min.css';
import './Spreadsheet.scss';

export interface HotTableRef {
  hotInstance: Handsontable | null;
}

export type HotTableProps = GridSettings & {
  licenseKey?: string;
  testId?: string;
};

export const HotTable = forwardRef<HotTableRef, HotTableProps>((props, ref: ForwardedRef<HotTableRef>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hotInstanceRef = useRef<Handsontable | null>(null);

  useImperativeHandle(ref, () => ({
    get hotInstance() {
      return hotInstanceRef.current;
    },
  }));

  // Initialize Handsontable
  useEffect(() => {
    if (_notNil(containerRef.current)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { licenseKey, testId, ...settings } = props;

      const hotInstance = new Handsontable(containerRef.current, {
        theme: 'ht-theme-classic',
        sanitizer: hotSanitizer,
        ...settings,
        // Core CSS is imported above so Spreadsheet.scss overrides load after it; runtime
        // injection would append it to the end of <head> and win the cascade.
        injectCoreCss: false,
        licenseKey: licenseKey ?? '',
        renderAllColumns: true,
      });
      // Handsontable 18 workaround: a fill-handle double click fills in before the mouseup reaches the
      // autofill plugin, which then skips clearing `mouseDownOnCellCorner`. The plugin redraws the fill
      // border on every mousemove while that flag is set, so it would follow the cursor afterwards.
      hotInstance.addHook('afterOnCellCornerDblClick', () => {
        hotInstance.getPlugin('autofill').mouseDownOnCellCorner = false;
      });
      // Handsontable 18 workaround: column header heights are measured from headers already stretched to
      // the last measured height, so a header that was once too tall never shrinks (e.g. a new column is
      // first drawn at the default width, wrapping a long title, before its own width applies). Clearing
      // the measurement and the stretched cells before each full render lets it measure their real height.
      hotInstance.addHook('beforeRender', (isForced) => {
        if (!isForced) {
          return;
        }
        hotInstance.view.renderSizeProbe.columnHeaderHeights.clear();
        hotInstance.rootElement
          .querySelectorAll<HTMLTableCellElement>('thead > tr > th:first-child')
          .forEach((headerCell) => headerCell.style.removeProperty('height'));
      });
      hotInstanceRef.current = hotInstance;
    }

    return () => {
      if (_notNil(hotInstanceRef.current)) {
        hotInstanceRef.current.destroy();
        hotInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (_notNil(hotInstanceRef.current)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { licenseKey, testId, ...settings } = props;
      hotInstanceRef.current.updateSettings(settings);
    }
  }, [props]);

  return <div ref={containerRef} data-test-component="HotTable" data-testid={props.testId} />;
});

HotTable.displayName = 'HotTable';
